import React, { useState, useEffect, useMemo } from 'react';
import { Button } from '../ui/button';
import { Lightbulb, X, Loader2 } from 'lucide-react';

// Difficulty: 6 wrong guesses + each revealed hint counts as one
// "step" toward the gallows — so hints have a real cost.
const MAX_STEPS = 6;
const ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');

/**
 * Hangman SVG that draws progressively as `steps` grows from 0 to MAX_STEPS.
 *   step 0: empty
 *   step 1: gallows base
 *   step 2: vertical post
 *   step 3: top beam + noose
 *   step 4: head
 *   step 5: body + arms
 *   step 6: legs (you died)
 */
const HangmanFigure = ({ steps, dead }) => {
  const stroke = dead ? '#FF3B3B' : '#FFFFFF';
  return (
    <svg viewBox="0 0 140 160" className="w-32 h-36 md:w-40 md:h-44 mx-auto" aria-label={`Hangman ${steps} of ${MAX_STEPS}`}>
      <defs>
        <filter id="hang-glow">
          <feGaussianBlur stdDeviation="2" result="b" />
          <feMerge><feMergeNode in="b" /><feMergeNode in="SourceGraphic" /></feMerge>
        </filter>
      </defs>
      <g stroke={stroke} strokeWidth="3" strokeLinecap="round" fill="none" filter={dead ? 'url(#hang-glow)' : undefined}>
        {/* step 1: gallows base */}
        {steps >= 1 && <line x1="10" y1="150" x2="90" y2="150" />}
        {/* step 2: vertical post */}
        {steps >= 2 && <line x1="30" y1="150" x2="30" y2="10" />}
        {/* step 3: top beam + rope */}
        {steps >= 3 && <>
          <line x1="30" y1="10" x2="90" y2="10" />
          <line x1="90" y1="10" x2="90" y2="30" />
        </>}
        {/* step 4: head */}
        {steps >= 4 && <circle cx="90" cy="42" r="12" />}
        {/* step 5: body + arms */}
        {steps >= 5 && <>
          <line x1="90" y1="54" x2="90" y2="100" />
          <line x1="90" y1="68" x2="74" y2="84" />
          <line x1="90" y1="68" x2="106" y2="84" />
        </>}
        {/* step 6: legs */}
        {steps >= 6 && <>
          <line x1="90" y1="100" x2="76" y2="125" />
          <line x1="90" y1="100" x2="104" y2="125" />
        </>}
        {/* X eyes when dead */}
        {dead && <>
          <line x1="85" y1="38" x2="89" y2="42" stroke={stroke} />
          <line x1="89" y1="38" x2="85" y2="42" stroke={stroke} />
          <line x1="91" y1="38" x2="95" y2="42" stroke={stroke} />
          <line x1="95" y1="38" x2="91" y2="42" stroke={stroke} />
        </>}
      </g>
    </svg>
  );
};

const HangmanGame = ({ rounds, onWin, onExit }) => {
  const [roundIdx, setRoundIdx] = useState(0);
  const round = rounds[roundIdx] || {};
  const word = (round.word || '').toUpperCase();
  const hints = round.hints || [];

  const [guessed, setGuessed] = useState(new Set());
  const [wrong, setWrong] = useState(0);
  const [hintsShown, setHintsShown] = useState(1); // start with 1 hint visible (free)
  const [status, setStatus] = useState('playing'); // 'playing' | 'won' | 'lost'

  useEffect(() => {
    // reset per round
    setGuessed(new Set());
    setWrong(0);
    setHintsShown(1);
    setStatus('playing');
  }, [roundIdx]);

  // EVERY hint AFTER the first costs you one step. So total "danger" = wrong + extraHints.
  const extraHints = Math.max(0, hintsShown - 1);
  const steps = Math.min(MAX_STEPS, wrong + extraHints);
  const stepsLeft = Math.max(0, MAX_STEPS - steps);

  const masked = useMemo(() => {
    if (!word) return '';
    return word
      .split('')
      .map((c) => (guessed.has(c) || c === ' ' ? c : '_'))
      .join(' ');
  }, [word, guessed]);

  useEffect(() => {
    if (status !== 'playing' || !word) return;
    const allFound = word.split('').every((c) => guessed.has(c) || c === ' ');
    if (allFound) setStatus('won');
    else if (steps >= MAX_STEPS) setStatus('lost');
  }, [guessed, steps, word, status]);

  const guess = (letter) => {
    if (status !== 'playing' || guessed.has(letter)) return;
    const next = new Set(guessed);
    next.add(letter);
    setGuessed(next);
    if (!word.includes(letter)) setWrong((w) => w + 1);
  };

  const revealHint = () => {
    if (status !== 'playing') return;
    if (hintsShown >= hints.length) return;
    if (steps >= MAX_STEPS) return;
    setHintsShown((n) => n + 1);
  };

  const nextRound = () => {
    if (roundIdx + 1 >= rounds.length) {
      onWin?.();
    } else {
      setRoundIdx((i) => i + 1);
    }
  };

  const giveUpRound = () => {
    if (roundIdx + 1 >= rounds.length) {
      onExit?.();
    } else {
      setRoundIdx((i) => i + 1);
    }
  };

  if (!word) {
    return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 text-[#FF007F] animate-spin" /></div>;
  }

  return (
    <div className="space-y-5" data-testid="hangman-game">
      <div className="flex items-center justify-between">
        <span className="text-[10px] uppercase tracking-widest text-gray-500 font-bold">
          Level {roundIdx + 1} of {rounds.length}
        </span>
        <span className={`text-[10px] uppercase tracking-widest font-bold ${stepsLeft <= 2 ? 'text-[#FF3B3B]' : 'text-gray-400'}`} data-testid="hangman-steps-left">
          {stepsLeft} steps left
        </span>
      </div>

      {/* The actual hangman drawing — grows with every wrong guess AND every extra hint */}
      <div className="rounded-2xl bg-white/[0.03] border border-white/10 py-4">
        <HangmanFigure steps={steps} dead={status === 'lost'} />
      </div>

      <div className="text-center py-6 px-2 rounded-2xl bg-white/[0.03] border border-white/10">
        <p
          className="text-3xl sm:text-4xl tracking-[0.4em] font-bold text-white font-['JetBrains_Mono'] break-all"
          data-testid="hangman-masked"
        >
          {masked}
        </p>
      </div>

      {/* Hints — each one after the first adds a step to the gallows */}
      <div className="space-y-2">
        {hints.slice(0, hintsShown).map((h, i) => (
          <div
            key={`hint-${roundIdx}-${i}`}
            className="flex items-start gap-2 text-sm text-gray-300 bg-[#FFD700]/8 border border-[#FFD700]/20 rounded-xl px-3 py-2"
            data-testid={`hangman-hint-${i}`}
          >
            <Lightbulb className="w-3.5 h-3.5 text-[#FFD700] flex-shrink-0 mt-0.5" />
            <span>
              {h}
              {i > 0 && <span className="ml-2 text-[10px] text-[#FF3B3B] uppercase tracking-wider font-bold">−1 step</span>}
            </span>
          </div>
        ))}
        {hintsShown < hints.length && status === 'playing' && (
          <button
            onClick={revealHint}
            className="text-xs text-[#FFD700] hover:underline flex items-center gap-1.5"
            data-testid="hangman-reveal-hint"
          >
            <Lightbulb className="w-3 h-3" /> Reveal next hint
            <span className="text-[#FF3B3B] uppercase tracking-wider font-bold text-[10px]">(costs 1 step)</span>
          </button>
        )}
      </div>

      {/* Keyboard */}
      {status === 'playing' && (
        <div className="grid grid-cols-7 sm:grid-cols-9 gap-1.5">
          {ALPHABET.map((letter) => {
            const used = guessed.has(letter);
            const isHit = used && word.includes(letter);
            const isMiss = used && !word.includes(letter);
            return (
              <button
                key={letter}
                onClick={() => guess(letter)}
                disabled={used}
                className={`h-10 rounded-lg text-sm font-bold tracking-wider transition-all ${
                  isHit ? 'bg-[#39FF14]/20 text-[#39FF14] border border-[#39FF14]/40'
                    : isMiss ? 'bg-[#FF3B3B]/15 text-[#FF3B3B] border border-[#FF3B3B]/30 opacity-60'
                    : 'bg-white/5 text-white border border-white/10 hover:border-[#FF007F]/40 hover:bg-[#FF007F]/10'
                } disabled:cursor-not-allowed`}
                data-testid={`hangman-key-${letter}`}
              >
                {letter}
              </button>
            );
          })}
        </div>
      )}

      {/* Result panel */}
      {status !== 'playing' && (
        <div
          className={`rounded-2xl p-4 border ${
            status === 'won' ? 'bg-[#39FF14]/10 border-[#39FF14]/30' : 'bg-[#FF3B3B]/10 border-[#FF3B3B]/30'
          }`}
          data-testid={`hangman-round-${status}`}
        >
          <p className={`font-bold mb-1 ${status === 'won' ? 'text-[#39FF14]' : 'text-[#FF3B3B]'}`}>
            {status === 'won' ? '✓ Nailed it!' : `✗ The word was ${word}`}
          </p>
          {roundIdx + 1 < rounds.length ? (
            <Button onClick={nextRound} className="mt-2 bg-white/10 hover:bg-white/20 text-white" data-testid="hangman-next-round">
              Next level →
            </Button>
          ) : status === 'won' ? (
            <Button onClick={nextRound} className="mt-2 bg-[#39FF14] hover:bg-[#39FF14]/90 text-black font-bold" data-testid="hangman-claim-win">
              Claim your coupon →
            </Button>
          ) : (
            <Button onClick={giveUpRound} variant="ghost" className="mt-2 text-gray-300" data-testid="hangman-give-up">
              Done
            </Button>
          )}
        </div>
      )}

      {status === 'playing' && (
        <button
          onClick={giveUpRound}
          className="text-xs text-gray-500 hover:text-gray-300 flex items-center gap-1 mx-auto"
          data-testid="hangman-skip"
        >
          <X className="w-3 h-3" /> Skip word
        </button>
      )}
    </div>
  );
};

export default HangmanGame;
